/* STEP: Contacts & POC Hierarchy (new parent only). Min 3 POCs; KAM, Weight,
   COD & Performance roles required. Each card = full POC form + role chips. */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import {useToast} from '../../components'
import {Field, Footer, scrollToFirstError} from './fieldKit'
import {POC_ROLES, roleLabel, blankPoc, validatePoc} from '../data'
import {apiError, POC_ROLE_ENUM} from '../../supplyMeta'
import {useSaveContactsMutation} from '../../../../redux/supplyApiSlice'

function PocCard({poc, i, errs, onField, onRole, onDelete, canDelete}) {
  return (
    <div className='card' style={{marginBottom: 12, background: '#FAFBFC'}}>
      <div className='card-hd'>
        <h3>
          {poc.name || 'POC #' + (i + 1)}
          {poc.roles.length ? ' · ' + roleLabel(poc.roles[0]) : ''}
        </h3>
        {canDelete ? (
          <button type='button' className='iconbtn' onClick={onDelete} aria-label='Remove POC'>
            <DeleteOutlineIcon style={{width: 16, height: 16}} />
          </button>
        ) : null}
      </div>
      <div className='card-pad'>
        <div className='grid' style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
          <Field label='Full name' value={poc.name} onChange={(v) => onField('name', v)} invalid={!!errs.name} help='First + last name of the POC.' />
          <Field label='Designation' value={poc.desig} onChange={(v) => onField('desig', v)} ph='Designation' invalid={!!errs.desig} />
          <Field label='Primary email' value={poc.email} onChange={(v) => onField('email', v)} ph='name@courier.com' invalid={!!errs.email} msg='Enter a valid email.' />
          <Field label='Mobile 1' value={poc.m1} onChange={(v) => onField('m1', v)} ph='10-digit mobile' invalid={!!errs.m1} msg='Enter a 10-digit mobile.' />
          <Field label='Mobile 2' value={poc.m2} onChange={(v) => onField('m2', v)} ph='10-digit mobile' invalid={!!errs.m2} msg={errs.m2 || 'Enter a 10-digit mobile.'} />
          <Field label='LinkedIn' opt value={poc.linkedin} onChange={(v) => onField('linkedin', v)} ph='https://linkedin.com/in/…' invalid={!!errs.linkedin} msg='Enter a valid linkedin.com URL.' />
        </div>
        <Field label='Notes' opt type='textarea' value={poc.notes} onChange={(v) => onField('notes', v)} ph='e.g. Prefers WhatsApp. Available Mon–Sat 9am–6pm.' />
        <div className={'field' + (errs.roles ? ' invalid' : '')} style={{marginBottom: 0}}>
          <label>
            Role(s) <span className='req'>*</span>
          </label>
          <div className='row wrap gap8' style={{display: 'flex', flexWrap: 'wrap', gap: 8}}>
            {POC_ROLES.map((r) => (
              <label key={r} className='chip' style={{cursor: 'pointer'}}>
                <input type='checkbox' checked={poc.roles.indexOf(r) >= 0} onChange={() => onRole(r)} />
                {roleLabel(r)}
              </label>
            ))}
          </div>
          <div className='vmsg'>Select at least one role.</div>
        </div>
      </div>
    </div>
  )
}

PocCard.propTypes = {
  poc: PropTypes.object,
  i: PropTypes.number,
  errs: PropTypes.object,
  onField: PropTypes.func,
  onRole: PropTypes.func,
  onDelete: PropTypes.func,
  canDelete: PropTypes.bool,
}

export default function StepContacts({W, dispatch, goBack, goNext}) {
  const toast = useToast()
  const [errors, setErrors] = useState({})
  const [busy, setBusy] = useState(false)
  const [saveContacts] = useSaveContactsMutation()
  const pocs = W.pocs

  const setPocs = (next) => dispatch({type: 'SET', slice: 'pocs', value: next})
  const field = (i, key, value) => {
    const next = pocs.map((p, ix) => (ix === i ? {...p, [key]: value} : p))
    setPocs(next)
  }
  const role = (i, r) => {
    const next = pocs.map((p, ix) => {
      if (ix !== i) return p
      const has = p.roles.indexOf(r) >= 0
      return {...p, roles: has ? p.roles.filter((x) => x !== r) : [...p.roles, r]}
    })
    setPocs(next)
  }
  const del = (i) => {
    if (pocs.length <= 3) {
      toast('Cannot remove — minimum 3 POCs required.', 'error')
      return
    }
    setPocs(pocs.filter((_, ix) => ix !== i))
  }

  const onNext = async () => {
    if (pocs.length < 3) {
      toast('Add at least 3 POCs before continuing.', 'error')
      return
    }
    const have = (r) => pocs.some((p) => p.roles.some((x) => x.indexOf(r) >= 0))
    if (!have('Key Account') || !have('Weight') || !have('COD') || !have('Performance')) {
      toast('Mandatory roles missing: KAM, Weight, COD and Performance Escalation must each be covered.', 'error')
      return
    }
    const errs = {}
    let ok = true
    pocs.forEach((p, i) => {
      const r = validatePoc(p)
      if (!r.ok) {
        errs[i] = r.errs
        ok = false
      }
    })
    setErrors(errs)
    if (!ok) {
      scrollToFirstError()
      return
    }
    if (!W.parentRef || !W.parentRef.id) {
      toast('Parent not created yet — go back to Courier Details and save it first.', 'error')
      return
    }
    const contacts = pocs.map((p) => ({
      contactId: null,
      name: p.name.trim(),
      designation: p.desig || '',
      email: p.email.trim(),
      mobilePrimary: p.m1,
      mobileSecondary: p.m2,
      linkedinUrl: p.linkedin || '',
      notes: p.notes || '',
      roles: p.roles.map((r) => POC_ROLE_ENUM[r]).filter(Boolean),
      isActive: true,
    }))
    setBusy(true)
    try {
      /* full replace; backend enforces min 3 POCs + mandatory role coverage and
         flips the parent DRAFT → ACTIVE on success */
      await saveContacts({id: W.parentRef.id, contacts}).unwrap()
      toast('Contacts saved — parent is now active.', 'success')
      goNext()
    } catch (e) {
      toast(apiError(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className='card card-pad'>
      <div className='row center' style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 14}}>
        <h3 style={{margin: 0}}>Contacts &amp; POC Hierarchy</h3>
        <span className='muted' style={{fontSize: 12, marginLeft: 'auto'}}>
          Minimum 3 POCs; KAM, Weight, COD &amp; Performance roles required.
        </span>
      </div>
      {pocs.map((poc, i) => (
        <PocCard
          key={i}
          poc={poc}
          i={i}
          errs={errors[i] || {}}
          canDelete={pocs.length > 3}
          onField={(k, v) => field(i, k, v)}
          onRole={(r) => role(i, r)}
          onDelete={() => del(i)}
        />
      ))}
      <button type='button' className='btn' style={{marginTop: 6}} onClick={() => setPocs([...pocs, blankPoc()])}>
        <AddIcon style={{width: 16, height: 16}} />
        Add another POC
      </button>
      <Footer step={W.step} disabled={busy} nextLabel={busy ? 'Saving…' : undefined} onBack={goBack} onNext={onNext} />
    </div>
  )
}

StepContacts.propTypes = {
  W: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  goNext: PropTypes.func.isRequired,
}
